import React from 'react'
import { useNavigate } from 'react-router-dom'

const CityVsCityLanding = () => {
  const navigate = useNavigate()

  // City vs City features array
  const features = [
    {
      "title": "Comprehensive City Data",
      "description": "Compare detailed statistics, demographics, cost of living, and quality of life metrics across multiple cities.",
      "icon": "📊"
    },
    {
      "title": "Side-by-Side Comparisons",
      "description": "View cities side-by-side with easy-to-read comparisons of key metrics and attributes.",
      "icon": "⚖️"
    },
    {
      "title": "Cost of Living Analysis",
      "description": "Compare housing costs, groceries, transportation, and other essential expenses between cities.",
      "icon": "💰"
    },
    {
      "title": "Quality of Life Metrics",
      "description": "Evaluate cities based on safety, healthcare, education, climate, and overall livability scores.",
      "icon": "🌟"
    },
    {
      "title": "Job Market Insights",
      "description": "Compare employment opportunities, average salaries, and industry presence across different cities.",
      "icon": "💼"
    },
    {
      "title": "Interactive Visualizations",
      "description": "Explore cities through interactive charts, maps, and visual comparisons that make data easy to understand.",
      "icon": "📈"
    },
    {
      "title": "Custom Comparisons",
      "description": "Create personalized comparisons focusing on the metrics that matter most to you.",
      "icon": "🎯"
    },
    {
      "title": "Save Your Favorites",
      "description": "Save cities you're interested in and build your own comparison lists for easy reference.",
      "icon": "⭐"
    }
  ]

  return (
    <div style={{ 
      width: '100%', 
      minHeight: '100vh',
      fontFamily: 'system-ui, sans-serif',
      color: '#2a2a2a',
      background: 'linear-gradient(135deg, #4A90E2 0%, #357ABD 100%)'
    }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        padding: '1rem 2rem',
        color: 'white'
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          ← Back to Home
        </button>
      </header>

      {/* Hero Section */}
      <section style={{
        textAlign: 'center',
        padding: '4rem 2rem',
        color: 'white'
      }}>
        {window.innerWidth <= 768 ? (
          // Mobile layout: logo above welcome message
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <img 
              src="/city-vs-city/logo.png" 
              alt="City vs City Logo" 
              style={{
                height: '80px',
                width: 'auto',
                borderRadius: '16px'
              }}
            />
            <h2 style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              margin: 0,
              textAlign: 'center'
            }}>
              Welcome to City vs City
            </h2>
          </div>
        ) : (
          // Desktop layout: logo and welcome message side by side
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            marginBottom: '1rem'
          }}>
            <h2 style={{
              fontSize: '3rem',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.3)',
              margin: 0
            }}>
              Welcome to City vs City
            </h2>
            <img 
              src="/city-vs-city/logo.png" 
              alt="City vs City Logo" 
              style={{
                height: '60px',
                width: 'auto',
                borderRadius: '12px'
              }}
            />
          </div>
        )}
        <p style={{
          fontSize: '1.2rem',
          marginBottom: '2rem',
          opacity: 0.9
        }}>
          Compare cities and discover which one is right for you
        </p>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.75rem 1.5rem',
          backgroundColor: 'white',
          color: '#4A90E2',
          borderRadius: '8px',
          fontSize: '1rem',
          fontWeight: '600',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
          opacity: 0.9
        }}>
          <svg width="20" height="24" viewBox="0 0 20 24" fill="currentColor" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.02 15.25 3.24 7.59 9.03 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.15c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
          </svg>
          Coming soon to the Apple App Store
        </div>
      </section>

      {/* Features Section */}
      <section id="features-section" style={{
        padding: '4rem 2rem',
        backgroundColor: 'white',
        color: '#2a2a2a'
      }}>
        <h3 style={{
          textAlign: 'center',
          fontSize: '2.5rem',
          marginBottom: '3rem',
          color: '#4A90E2'
        }}>
          City vs City Features
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {features.map((feature, index) => (
            <div key={index} style={{
              padding: '2rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{feature.icon}</div>
              <h4 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>{feature.title}</h4>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Support Section */}
      <section style={{
        padding: '4rem 2rem',
        backgroundColor: 'white',
        color: '#2a2a2a'
      }}>
        <h3 style={{
          textAlign: 'center',
          fontSize: '2.5rem',
          marginBottom: '3rem',
          color: '#4A90E2'
        }}>
          Support & Contact
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            padding: '2rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '12px',
            textAlign: 'center'
          }}>
            <h4 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Email Support</h4>
            <p>Get help at signalpeaksw@outlook.com</p>
          </div>
          <div 
            onClick={() => navigate('/city-vs-city/help')}
            style={{
              padding: '2rem',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          >
            <h4 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Documentation</h4>
            <p>Comprehensive guides and tutorials</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer style={{
        padding: '2rem',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '2rem',
          marginBottom: '1rem',
          flexWrap: 'wrap'
        }}>
          <a href="/city-vs-city/privacy.html" style={{
            color: 'white',
            textDecoration: 'none',
            opacity: 0.8
          }}>Privacy Policy</a>
          <a href="/city-vs-city/terms.html" style={{
            color: 'white',
            textDecoration: 'none',
            opacity: 0.8
          }}>Terms of Service</a>
          <a href="/city-vs-city/support.html" style={{
            color: 'white',
            textDecoration: 'none',
            opacity: 0.8
          }}>Support & FAQ</a>
          <a href="mailto:signalpeaksw@outlook.com" style={{
            color: 'white',
            textDecoration: 'none',
            opacity: 0.8
          }}>Contact</a>
        </div>
        <p style={{ opacity: 0.7, fontSize: '0.9rem' }}>
          &copy; {new Date().getFullYear()} Signal Peak Software. All rights reserved.
        </p>
      </footer>
    </div>
  )
}

export default CityVsCityLanding
